import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { T } from "react-translator-component";
import { createNewPostState } from "../util/CreatePost"
import { useAppDispatch } from "../redux/store";
import { getGroups } from "../redux/Reducers/groupReducer";
import { getCategory } from "../redux/Reducers/categoriesReducer";
import appServices from "../services/app-services/appServices";
import LoadingModal from "../components/LoadingModal";
import DropDown from "../components/DropDown";
import PostCard from "../components/lists/PostCard";
import TabComponent from "../components/Phase2/TabComponent";
import MebukuIdUpgradeBanner from "../components/MebukuIdUpgradeBanner";
import { ErrorMessageModal } from "../components/ErrorMessageModal";
import LoadMoreButton from "../components/LoadMoreButton";
import { styles } from "./styles";
import "../App.css";
import { Button, SelectChangeEvent } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";

type SortType = "created" | "commnet"
type OrderType = "asc" | "desc"

interface PostParams {
  category_id: number | null,
  page: number,
  sort_type: SortType,
  order: OrderType,
  group_id: number | null
}

export default function CategoryListPage() {
  const sortOptions = [
    { value: "created_desc", name: T("sort_by_new") },
    { value: "created_asc", name: T("sort_by_old") },
    { value: "comment_desc", name: T("sort_by_most_comments") },
    { value: "comment_asc", name: T("sort_by_least_comments") },
  ]

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: any) => state.userAuth);
  const { groups } = useSelector((state: any) => state.groups);
  const { categories } = useSelector((state: any) => state.categories);
  const [loader, setLoader] = useState(false);
  const [communityPost, setCommunityPosts] = useState<any>([]); //using now for all categories
  const [sortedCategories, setSortedCategories] = useState<any>([]);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [groupId, setGroupId] = useState<number | null>(null);
  const [selectSortOption, setSelectSortOption] = useState<string>("");
  const [pagination, setPagination] = useState<any>({});
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string[]>([]);

  const apiCalls = async () => {
    setLoader(true);
    await dispatch(getGroups());
    await dispatch(getCategory());
    setLoader(false);
  };

  useEffect(() => {
    apiCalls();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const sortOrder = parseSortOrder()

    getCategoryPosts({
      category_id: categoryId,
      page: 1,
      sort_type: sortOrder.sortType,
      order: sortOrder.order,
      group_id: groupId
    });
  }, [categoryId, selectSortOption, groupId])

  const getCategoryPosts = async (params: PostParams) => {
    try {
      setLoader(true);
      const res = await appServices.getApprovedCategoryPosts(params);
      if (params.page === 1) {
        setCommunityPosts(res.posts)
      } else {
        setCommunityPosts([...communityPost, ...res.posts])
      }
      setPagination(res.pagination)
    } catch (error: any) {
      setErrMsg([error?.response?.data?.message ?? T("api_unknown_error")])
      setShowErrorModal(true)
    } finally {
      setLoader(false);
    }
  }

  useEffect(() => {
    if (categories?.length > 0) {
      const temp = [...categories].sort(
        (a: { id: number }, b: { id: number }) =>
          a.id < b.id ? -1 : a.id > b.id ? 1 : 0
      );
      setSortedCategories(temp);
    }
  }, [categories]);

  const handleSelectGroup = (event: SelectChangeEvent) => {
    setGroupId(event.target.value ? Number(event.target.value) : null);
  };

  const handleSelectCategory = (c: any) => {
    if (c !== 0) {
      const v = sortedCategories[c - 1];
      setCategoryId(v.id);
    } else {
      setCategoryId(null)
    }
  };

  const handleSort = (event: SelectChangeEvent) => {
    setSelectSortOption(event.target.value);
  };

  const loadMore = () => {
    if (!pagination?.next_page || pagination?.next_page < 2) return

    const sortOrder = parseSortOrder()

    getCategoryPosts({
      category_id: categoryId,
      page: pagination.next_page,
      sort_type: sortOrder.sortType,
      order: sortOrder.order,
      group_id: groupId
    });
  };

  const gotoPost = () => {
    navigate(
      "/createPost",
      createNewPostState("UserPost", user, undefined, [], dispatch)
    );
  };

  const parseSortOrder = () => {
    const [ type, direction ] = selectSortOption.split("_");
    const sortType = type || "created"
    const orderType = direction || "desc"

    return {
      sortType: sortType as SortType,
      order: orderType as OrderType,
    }
  }

  const genreOptions = () => {
    if (!groups || groups?.length === 0) return []

    return [
      { value: "", name: T("clear_filter") },
      ...groups
    ]
  }

  return (
    <div style={styles.communityContainer}>
      <LoadingModal visible={loader} />

      <div style={styles.communityBox}>
        <div style={styles.createPostBtnBox}>
          <Button
            onClick={() => gotoPost()}
            style={styles.createPostBtn}
            size={"large"}
            variant="contained"
            endIcon={<FontAwesomeIcon icon={faChevronRight} />}
          >
            <span style={styles.createPostBtnText}>
              {T("post")}
            </span>
          </Button>
        </div>

        <TabComponent onChange={handleSelectCategory} />

        <DropDown
          title={T("group")}
          value={groupId ?? ""}
          options={genreOptions()}
          onChange={handleSelectGroup}
        />
        <DropDown
          title={T("sort")}
          value={selectSortOption}
          options={sortOptions}
          onChange={handleSort}
        />
      </div>

      <div>
        {communityPost.map((item: any, index: number) => (
          <PostCard
            post={item}
            showPostMenu={true}
            key={index}
          />
        ))}
      </div>

      <LoadMoreButton show={pagination?.next_page > 0} onClick={loadMore} />

      <MebukuIdUpgradeBanner />

      <ErrorMessageModal
        open={showErrorModal}
        messages={errMsg}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
}
